import '../product.scss';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import {
  Product,
  ProductListRequest,
  ProductType,
  ProductUpdateRequest,
} from '../../../clients/quipli-service/product/product';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { ActivateAction, DeactivateAction, DeleteAction, EditAction } from '../../../components/actions/actions';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { Button } from '../../../components/form/button';
import { SelectForm } from '../../../components/select-form/select-form';
import { Category } from '../../../clients/quipli-service/category/category';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { toast } from '../../../util/toasts';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { TenantContext } from '../../../context/tenant-context';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { useCategoryApiV1 } from '../../../clients/quipli-service/category/category-api-v1';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { ProductPrice, RentalProductPrice, SaleProductPrice } from '../../../clients/quipli-service/pricing/pricing';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { Dictionary, groupBy } from 'lodash';
import { usePricingApiV1 } from '../../../clients/quipli-service/pricing/pricing-api-v1';
import { LocationSelect } from '../../location/select/location-select';

export const ProductList = () => {
  const { searchProducts, updateProduct } = useAPI();
  const { listCategories } = useCategoryApiV1();
  const { listPrices } = usePricingApiV1();

  const [isListLoading, setListLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [actionModal, setActionModal] = useState<ReactNode>();
  const { contextPath } = useContext(TenantContext);
  const [pagination, setPagination] = useState<PaginationData>();

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<ProductListRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      categoryId: params.has('categoryId') ? parseInt(params.get('categoryId')!) : undefined,
      locationId: params.has('locationId') ? parseInt(params.get('locationId')!) : undefined,
      isActive: params.has('isActive') ? params.get('isActive')! === 'true' : undefined,
      createdFrom: params.has('createdFrom') ? DateTime.fromISO(params.get('createdFrom')!) : undefined,
      createdTo: params.has('createdTo') ? DateTime.fromISO(params.get('createdTo')!) : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    (_request) => {
      setListLoading(true);

      const { keyword, categoryId, locationId, isActive, createdFrom, createdTo, pageSize, pageNumber } = _request;
      searchProducts({
        types: [ProductType.Product],
        keyword,
        categoryIds: categoryId ? [categoryId] : undefined,
        locationIds: locationId ? [locationId] : undefined,
        isActive: isActive,
        createdFrom: createdFrom?.toISODate(),
        createdTo: createdTo?.toISODate(),
        pageSize,
        pageNumber,
      })
        .then(({ pagination, items }) => {
          setPagination(pagination);
          listPrices({
            type: 'All',
            productIds: items.map((i) => i.id),
          }).then((prices) => {
            const pricesMap: Dictionary<ProductPrice[]> = groupBy(prices, 'productId');
            setData(items.map((item) => buildDataRow(item, pricesMap[item.id] ?? [])));
            setListLoading(false);
          });
        })
        .catch(() => {});
    },
  );

  // Vars
  const columns: DataTableColumn[] = [
    { key: 'name', label: 'Name' },
    { key: 'for', label: 'For' },
    { key: 'salePrice', label: 'Sale Price' },
    { key: 'rentalPrice', label: 'Rental Price' },
    { key: 'totalStock', label: 'Total Stock' },
    { key: 'saleStock', label: 'Sale Stock' },
    { key: 'rentalStock', label: 'Rental Stock' },
    { key: 'outageStock', label: 'Outage Stock' },
    { key: 'date', label: 'Date' },
    { key: 'buttons', label: 'Actions', fixedAsLast: true },
  ];
  const productStatusValues: { label: string; value: 'Yes' | 'No' }[] = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ];

  // Callbacks
  async function doUpdate(i: number, update: ProductUpdateRequest) {
    const productId = i;
    await updateProduct(productId, update)
      .then(() => {
        toast('Successfully updated!', 'success');
        fetchCallback();
      })
      .catch((err) => {
        const errMsg = err.status === 400 && err.message ? err.message.msg : 'Error updating product!';
        toast(errMsg, 'error');
      });
  }

  useEffect(() => {
    Promise.all([listCategories()]).then(([categories]) => {
      setCategories(categories.filter((c) => c.isActive && !c.isDeleted));
    });
  }, []);

  //Helpers
  const buildDataRow = (p: Product, prices: ProductPrice[]) => {
    const salePriceUnits = prices.filter((p) => p.type === OrderType.Sale) as SaleProductPrice[];
    const rentPriceUnits = prices.filter((p) => p.type === OrderType.Rental) as RentalProductPrice[];
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {p.name}
          </Typography>
        </>
      ),
      fields: {
        name: (
          <div className="product-container">
            <Typography type="text-sm" className={`${p.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}`}>
              {p.name}
            </Typography>
          </div>
        ),
        for: (
          <Typography type="text-sm" className={`${p.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}`}>
            {p.allowRent ? 'Rent' : ''} {p.allowSale ? 'Sale' : ''}
          </Typography>
        ),
        salePrice: (
          <Typography type="text-sm" className={`${p.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}`}>
            {salePriceUnits.length > 0 ? '$' + (salePriceUnits[0].price / 100).toFixed(2) : '$0.00'}
          </Typography>
        ),
        rentalPrice: buildListPrices(
          rentPriceUnits,
          p.isActive
            ? { priceColor: 'text-[#1D252C]', unitColor: 'text-[#667085]' }
            : { priceColor: 'text-[#98A2B3]', unitColor: 'text-[#98A2B3]' },
        ),
        totalStock: (
          <Typography type="text-sm" className={`${p.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}`}>
            {p.totalStock}
          </Typography>
        ),
        saleStock: (
          <Typography type="text-sm" className={`${p.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}`}>
            {p.saleStock}
          </Typography>
        ),
        rentalStock: (
          <Typography type="text-sm" className={`${p.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}`}>
            {p.rentalStock}
          </Typography>
        ),
        outageStock: (
          <Typography type="text-sm" className={`${p.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}`}>
            {p.outageStock ? p.outageStock : 0}
          </Typography>
        ),
        date: (
          <>
            <div>
              <Typography type="text-sm" className={p.isActive ? 'text-[#1D252C]' : 'text-[#98A2B3]'}>
                {p.createdAt.toFormat('LLL-dd')}
              </Typography>
              <Typography type="text-sm" className={p.isActive ? 'text-[#667085]' : 'text-[#98A2B3]'}>
                {p.createdAt.toFormat('hh:mm a').toLowerCase()}
              </Typography>
            </div>
          </>
        ),
        buttons: (
          <div className="button-group">
            {p.isActive ? (
              <DeactivateAction
                onClick={() => doUpdate(p.id, { isActive: false, isDeleted: p.isDeleted })}
                data-cy="hide_product"
              />
            ) : (
              <ActivateAction
                onClick={() => doUpdate(p.id, { isActive: true, isDeleted: p.isDeleted })}
                data-cy="show_product"
              />
            )}
            <EditAction
              onClick={() => {
                const url = `${contextPath}manager/products/form/${p.id}`;
                window.open(url, '_self')?.focus();
              }}
              data-cy="edit_product"
            />

            <DeleteAction
              onClick={() =>
                setActionModal(
                  <ConfirmModal
                    isOpen={true}
                    close={() => setActionModal(undefined)}
                    onConfirm={() => {
                      doUpdate(p.id, { isActive: p.isActive, isDeleted: true });
                      setActionModal(undefined);
                    }}
                    headerIcon={<i className="fa fa-trash-can delete-icon"></i>}
                    prompt={`Are you sure you want to delete product ${p.name}?`}
                  />,
                )
              }
              data-cy="delete_product"
            ></DeleteAction>
          </div>
        ),
        id: p.id,
      },
      redirectTo: `products/form/${p.id}`,
    };
  };

  const buildListPrices = (prices: RentalProductPrice[], colors: { priceColor: string; unitColor: string }) => {
    const priceRates: any = [];

    prices
      .sort((a, b) => a.durationMinutes - b.durationMinutes)
      .forEach((price) => {
        if (price.recurringType === null && price.featured) {
          let durValue: number | string;
          let durUnit: any = price.durationUnit;

          if (price.durationValue === 1) {
            durValue = '';
          } else {
            durValue = ` ${price.durationValue}`;
            durUnit = `${price.durationUnit}s`;
          }

          const unit = `Per${durValue} ${durUnit}`;
          const priceRate = {
            prod_id: price.productId,
            price: (price.price / 100).toFixed(2),
            desc: unit,
            unit,
            show_unit: price.featured,
          };
          priceRates.push(priceRate);
        }
      });

    return (
      <div>
        {priceRates.length > 0 ? (
          <ul className="product-prices">
            {priceRates.map((priceRate: any) => (
              <li>
                <span className={`quipli-v2 text-sm regular ${colors.priceColor}`}>{`$${priceRate.price}`}</span>{' '}
                {priceRate.show_unit && <span className={colors.unitColor}>{priceRate.desc}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p>NA</p>
        )}
      </div>
    );
  };

  return (
    <div className="product-list">
      <div className="container-v2">
        <div className="flex justify-between items-center">
          <Typography type="display-sm" weight="medium">
            Manage Products
          </Typography>
        </div>

        <div className="flex items-center justify-between search-bar-padding">
          <SearchBar
            placeholder="Search Products"
            defaultValue={request.keyword}
            setRequest={setRequest}
            data-cy="search_products"
          />
          <div className="hidden md:block">
            <div className="flex flex-end">
              <Button
                className="button-yellow sm"
                data-cy="add_products"
                onClick={() => {
                  const url = `${contextPath}manager/products/form`;
                  window.open(url, '_self')?.focus();
                }}
              >
                <i className="fa fa-plus" />
                Add Products
              </Button>
            </div>
          </div>
        </div>

        <div className="block md:hidden">
          <div className="flex pt-5">
            <Button
              className="button-yellow sm"
              data-cy="add_products"
              onClick={() => {
                const url = `${contextPath}manager/products/form`;
                window.open(url, '_self')?.focus();
              }}
            >
              <i className="fa fa-plus" />
              Add Products
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 py-5 [&>*]:shrink-0">
          <div>
            <SelectForm
              options={categories}
              onChange={(c) => {
                setRequest((prevState) => ({ ...prevState, categoryId: c?.id }));
              }}
              getOptionLabel={(c) => c.name}
              getOptionValue={(c) => `${c.id}`}
              isClearable={true}
              placeholder="Category"
              value={request.categoryId ? categories.find((e) => e.id === request.categoryId) : undefined}
            />
          </div>
          <div>
            <LocationSelect
              setLocation={(l) => {
                setRequest((prevState) => ({ ...prevState, locationId: l?.id }));
              }}
              clearable={true}
              dataCy="location-select"
            />
          </div>
          <div>
            <SelectForm
              options={productStatusValues}
              onChange={(ps) => {
                setRequest((prevState) => ({ ...prevState, isActive: ps ? ps.value === 'Yes' : undefined }));
              }}
              isClearable={true}
              placeholder="Active"
              value={
                request.isActive
                  ? productStatusValues.find(
                      (e) => (e.value === 'Yes' && request.isActive) || (e.value === 'No' && !request.isActive),
                    )
                  : undefined
              }
            />
          </div>
          <div className="date-picker-filter">
            <DatePicker
              value={request.createdFrom}
              onChange={(from) => setRequest({ ...request, createdFrom: from })}
              placeholder="Date From"
              clearable
            />
          </div>
          <div className="date-picker-filter">
            <DatePicker
              value={request.createdTo}
              onChange={(to) => setRequest({ ...request, createdTo: to })}
              placeholder="Date To"
              clearable
            />
          </div>
        </div>

        <DataTable
          isLoading={isListLoading}
          columnNames={columns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          cypressPrevious="previous_products_page"
          cypressNext="next_products_page"
        />
        {actionModal}
      </div>
    </div>
  );
};
